/**
 * WB Agent API client — thin wrapper around the wb_agent HTTP routes.
 *
 * Auth is handled the same way as LlmProxyClient: Basic auth header with
 * entity/project passed as query or body params, credentials: 'include'
 * for cookie-based sessions.
 */

import {getCookie} from '@wandb/weave/common/util/cookie';
import {urlPrefixed} from '@wandb/weave/config';
import type {components, operations, paths} from './generated/wb-agent-types';

// =============================================================================
// Types — generated from services/wb_agent/openapi.json
// =============================================================================

export type TurnState = components['schemas']['TurnState'];

export const TERMINAL_STATES: TurnState[] = [
  'completed',
  'errored',
  'cancelled',
];

export type MessageRecord = components['schemas']['MessageRecord'];
export type ToolCall = components['schemas']['ToolCall'];
export type ToolCallInvocationRecord =
  components['schemas']['ToolCallInvocationRecord'];
export type ToolCallResponseRecord =
  components['schemas']['ToolCallResponseRecord'];
export type ToolCallRecord = NonNullable<
  components['schemas']['TurnResponse']['tool_calls']
>[number];
export type TurnResponse = components['schemas']['TurnResponse'];
export type CreateTurnRequest = components['schemas']['CreateTurnRequest'];
export type SetFeedbackRequest = components['schemas']['SetFeedbackRequest'];
export type IsAvailableResponse = components['schemas']['IsAvailableResponse'];

// =============================================================================
// Route-level type safety — auto-derived from the generated paths/operations
// =============================================================================

/** Extract the first 2xx JSON response type from an operation. */
type SuccessResponseOf<R> = R extends {
  200: {content: {'application/json': infer T}};
}
  ? T
  : R extends {201: {content: {'application/json': infer T}}}
    ? T
    : R extends {202: {content: {'application/json': infer T}}}
      ? T
      : never;
type SuccessResponse<Op> = Op extends {responses: infer R}
  ? SuccessResponseOf<R>
  : never;

/** Query params minus auto-injected entity/project. */
type ExtraParams<Op> = Op extends {parameters: {query: infer Q}}
  ? Omit<Q, 'entity' | 'project'>
  : Record<string, never>;

/** JSON request body type. */
type RequestBody<Op> = Op extends {
  requestBody: {content: {'application/json': infer B}};
}
  ? B
  : never;

/** Strip the /api/v1 prefix that the client prepends automatically. */
type StripApiPrefix<P extends string> = P extends `/api/v1${infer Rest}`
  ? Rest
  : never;

/** True when a path contains {param} segments (dynamic routes). */
type HasPathParams<P extends string> = P extends `${string}{${string}}${string}`
  ? true
  : false;

/**
 * GET route map — auto-derived from paths with a `get` operation and no path
 * parameters.  Maps stripped path → { response, params }.
 */
type GETRoutes = {
  [P in keyof paths & string as HasPathParams<P> extends true
    ? never
    : paths[P] extends {get: unknown}
      ? StripApiPrefix<P>
      : never]: paths[P] extends {get: infer Op}
    ? {response: SuccessResponse<Op>; params: ExtraParams<Op>}
    : never;
};

/**
 * POST route map — auto-derived from paths with a `post` operation and no
 * path parameters.  Maps stripped path → { response, body, params }.
 */
type POSTRoutes = {
  [P in keyof paths & string as HasPathParams<P> extends true
    ? never
    : paths[P] extends {post: unknown}
      ? StripApiPrefix<P>
      : never]: paths[P] extends {post: infer Op}
    ? {
        response: SuccessResponse<Op>;
        body: RequestBody<Op>;
        params: ExtraParams<Op>;
      }
    : never;
};

/** Query parameters accepted by GET /turns (entity/project auto-injected). */
export type QueryTurnsParams = ExtraParams<
  operations['query_turns_api_v1_turns_get']
>;

// =============================================================================
// Client
// =============================================================================

export interface WbAgentClientConfig {
  baseUrl: string; // e.g. "http://localhost:8090"
  entity: string;
  project: string;
}

export class WbAgentClient {
  private readonly baseUrl: string;
  readonly entity: string;
  readonly project: string;

  constructor(config: WbAgentClientConfig) {
    this.baseUrl = config.baseUrl.replace(/\/+$/, '');
    this.entity = config.entity;
    this.project = config.project;
  }

  // ── Helpers ──────────────────────────────────────────────────────────

  private buildHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      Authorization: 'Basic ' + btoa(':'),
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
    if (getCookie('use_admin_privileges') === 'true') {
      headers['use-admin-privileges'] = 'true';
    }
    return headers;
  }

  private scopedUrl(
    path: string,
    params: Record<string, unknown> = {}
  ): string {
    const qs = new URLSearchParams({
      entity: this.entity,
      project: this.project,
    });
    for (const [key, value] of Object.entries(params)) {
      if (value != null) {
        qs.set(key, String(value));
      }
    }
    return `${this.baseUrl}/api/v1${path}?${qs.toString()}`;
  }

  private async request<T>(
    method: string,
    url: string,
    body?: unknown,
    signal?: AbortSignal
  ): Promise<T> {
    // eslint-disable-next-line wandb/no-unprefixed-urls
    const response = await fetch(urlPrefixed(url), {
      method,
      credentials: 'include',
      headers: this.buildHeaders(),
      body: body ? JSON.stringify(body) : undefined,
      signal,
    });

    if (!response.ok) {
      let errorBody: string;
      try {
        errorBody = await response.text();
      } catch {
        errorBody = response.statusText;
      }
      throw new Error(`WbAgent API ${response.status}: ${errorBody}`);
    }

    return (await response.json()) as T;
  }

  // ── Generic HTTP methods ───────────────────────────────────────────

  /** Typed overload — fixed paths, fully inferred response & params. */
  get<P extends keyof GETRoutes & string>(
    path: P,
    params?: GETRoutes[P]['params'],
    signal?: AbortSignal
  ): Promise<GETRoutes[P]['response']>;
  /** Fallback overload — dynamic paths, caller specifies T. */
  get<T>(
    path: string,
    params?: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<T>;
  async get(
    path: string,
    params?: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<unknown> {
    return this.request('GET', this.scopedUrl(path, params), undefined, signal);
  }

  /** Typed overload — fixed paths, fully inferred response, body & params. */
  post<P extends keyof POSTRoutes & string>(
    path: P,
    body?: POSTRoutes[P]['body'],
    params?: POSTRoutes[P]['params'],
    signal?: AbortSignal
  ): Promise<POSTRoutes[P]['response']>;
  /** Fallback overload — dynamic paths, caller specifies T. */
  post<T>(
    path: string,
    body?: unknown,
    params?: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<T>;
  async post(
    path: string,
    body?: unknown,
    params?: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<unknown> {
    return this.request('POST', this.scopedUrl(path, params), body, signal);
  }
}
